<template>
  <article v-if="collection">
    <ArtworkNavHeader :back="false" :filters="filters"></ArtworkNavHeader>
    <section :class="[$style.article, 'container']">
      <h1>Collection Online</h1>
      <ProcessContent
        v-if="collection"
        :content="collection.content"
        wrapper="section"
      ></ProcessContent>
    </section>
  </article>
</template>

<script setup lang="ts">
import ArtworkNavHeader from '@/components/artwork/ArtworkNavHeader.vue';
import { BootStrap } from '@/types/global.interface';
import { collectionOnlineQuery } from '@/queries/GetCollectionOnline';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import { inject, ref } from 'vue';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { storeToRefs } from 'pinia';
import { useArtworkFilters } from '@/composables/ArtworkFilters.js';
import { useMainStore } from '@/stores/mainStore.js';
import { useQuery } from 'villus';

interface Collection {
  content: string;
  excerpt: string;
  title: string;
  uri: string;
}

const bootstrap = inject<BootStrap>('bootstrap');

const { getFilters } = useArtworkFilters();
const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const store = useMainStore();
const { useBootstrap } = storeToRefs(store);

const collection = ref<Collection>({});
const filters = ref({});
const shouldSkip = ref(true);

const { data } = useQuery({
  query: collectionOnlineQuery,
  skip: shouldSkip,
  variables: {
    type: 'URI',
    id: 'collection-online',
  },
  onData: (data) => {
    if (data.page && Object.keys(data.page).length) {
      filters.value = getFilters(data);
      collection.value = data.page;
      setMetaTitle(data.page.title);
      setMetaDescription(data.page.excerpt);
      dataLoaded();
    }

    if (!data.page || Object.keys(data.page).length === 0) {
      handleError('CollectionOnline', window.location.pathname);
    }
  },
});

if (useBootstrap.value) {
  shouldSkip.value = true;

  if (
    bootstrap &&
    bootstrap.initial &&
    bootstrap.initial.queried_object &&
    bootstrap.initial.queried_object.pagedata &&
    bootstrap.initial.queried_object.pagedata !== null
  ) {
    const { page } = bootstrap.initial.queried_object.pagedata;

    filters.value = getFilters(bootstrap.initial.queried_object.pagedata);
    collection.value = page;
    setMetaTitle(collection.value.title);
    setMetaDescription(collection.value.excerpt);
    dataLoaded();
  }

  if (
    bootstrap &&
    bootstrap.initial &&
    (!bootstrap.initial.queried_object || !bootstrap.initial.queried_object.pagedata)
  ) {
    shouldSkip.value = false;
  }
}

if (!useBootstrap.value) {
  shouldSkip.value = false;
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: collectionOnlineQuery,
          variables: {
            id: 'collection-online',
            type: 'URI',
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.article {
  @include space-48-64-above;

  > h1 {
    margin: 0;
  }
}

@media (min-width: $break-point) {
  .article {
    @include grid;

    & {
      margin-top: 0;
    }

    > h1 {
      grid-column: 1 / span 6;
    }

    > nav {
      grid-column: 1 / span 8;
    }

    > section {
      @include grid-content;

      & {
        grid-column: span 12;
      }
    }
  }
}
</style>
